import React, { useEffect, useState } from 'react';

import {
  Button,
  Card,
  CardSizes,
  CardStack,
  CheckboxButton,
  CheckboxButtonGroup,
  FormControlLabel,
  FormGroup,
  Input,
  Logo,
  LoadingOverlay,
} from '@user-interviews/ui-design-system';

import { ErrorRenderer } from 'lib/errors';
import * as propTypes from 'lib/prop_types';
import { routes } from 'lib/routes';

import { DopeForm } from 'components/dope_form';
import { TimezoneSelect } from 'components/timezone_select';
import useHttp from 'hooks/use_http';

import StudyDetailsAccordion from './study_details_accordion';

// oxlint-disable-next-line local-rules/css_modules
import './apply_sign_up.css';

// Disabled when adding max-props rule.
// Future work should stay under 6 props
// oxlint-disable-next-line local-rules/max-props
function ApplySignUp({
  dataConsentNoticeText,
  email,
  errors,
  firstName,
  isDataConsentNoticeActive,
  isDataConsentNoticeChecked,
  isDataConsentNoticeErrorPresent,
  lastName,
  logoUrl,
  projectAccessCode,
  projectId,
  teamName,
  timezoneName,
}) {
  const { get, loading } = useHttp();
  const [projectDetails, setProjectDetails] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataConsentNoticeChecked, setDataConsentNoticeChecked] = useState(
    isDataConsentNoticeChecked,
  );

  const handleDCNCheckboxChange = () =>
    setDataConsentNoticeChecked((prev) => !prev);

  const handleSubmit = () => {
    setIsSubmitting(true);
  };

  const APPLY_SIGN_UP_FORM_POSSIBLE_ERROR_INPUT_IDS = {
    first_name: 'first-name',
    last_name: 'last-name',
    email: 'email',
    timezone_name: 'timezone-name',
    accept_data_consent_notice: 'data-consent-notice-checkbox',
  };

  const focusOnFirstInputError = (key) => {
    const firstErroredInputId =
      APPLY_SIGN_UP_FORM_POSSIBLE_ERROR_INPUT_IDS[key];
    const firstErroredElement = document.getElementById(firstErroredInputId);
    if (firstErroredElement) {
      firstErroredElement.focus();
    }
  };

  const findFirstErroredInput = () => {
    setTimeout(() => {
      focusOnFirstInputError(Object.keys(errors)[0]);
    }, '0');
  };

  useEffect(() => {
    if (errors) {
      findFirstErroredInput();
    }

    async function fetchProjectDetails() {
      const url = routes.api_project_participation_details_path({
        project_id: projectId,
      });

      const response = await get(url, {
        onError: (error) =>
          ErrorRenderer.alert(error, 'fetching participation details'),
      });

      if (response) {
        setProjectDetails(response);
      }
    }

    fetchProjectDetails();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, projectId]);

  return (
    <main className="ApplySignUp">
      <CardStack size={CardSizes.SMALL}>
        <div className="ApplySignUp__header">
          {logoUrl && (
            <Logo alignment="center" alt={`${teamName} logo`} src={logoUrl} />
          )}
        </div>
        <h1 className="ApplySignUp__header__title">
          You’ve been invited to participate in a study
        </h1>
        <LoadingOverlay visible={loading} />

        <div className="ApplySignUp__form">
          {projectDetails && (
            <Card noPadding size={CardSizes.SMALL}>
              <StudyDetailsAccordion
                description={projectDetails.publicDescription}
                incentive={projectDetails.incentive}
                noIncentive={projectDetails.noIncentive}
                requiresDocumentSignature={
                  projectDetails.requiresDocumentSignature
                }
                sessionLength={projectDetails.sessionLength}
                title={projectDetails.publicTitle}
              />
            </Card>
          )}
          <DopeForm
            action={routes.submit_projects_apply_path({
              access_code: projectAccessCode || 'FAKE_ACCESS',
            })}
            onSubmit={handleSubmit}
          >
            <Card className="ApplySignUp__Card" size={CardSizes.SMALL}>
              <FormGroup
                errors={{ firstName: errors.first_name }}
                inputKey="firstName"
                label="First name"
                labelHtmlFor="first-name"
                required
              >
                <Input
                  aria-describedby={
                    errors.first_name && 'form-errors-firstName'
                  }
                  aria-invalid={!!errors.first_name}
                  aria-required="true"
                  defaultValue={firstName}
                  id="first-name"
                  name="first_name"
                />
              </FormGroup>
              <FormGroup
                errors={{ lastName: errors.last_name }}
                inputKey="lastName"
                label="Last name"
                labelHtmlFor="last-name"
                required
              >
                <Input
                  aria-describedby={errors.last_name && 'form-errors-lastName'}
                  aria-invalid={!!errors.last_name}
                  aria-required="true"
                  defaultValue={lastName}
                  id="last-name"
                  name="last_name"
                />
              </FormGroup>
              <FormGroup
                errors={{ email: errors.email }}
                inputKey="email"
                label="Email"
                labelHtmlFor="email"
                required
              >
                <Input
                  aria-describedby={errors.email && 'form-errors-email'}
                  aria-invalid={!!errors.email}
                  aria-required="true"
                  defaultValue={email}
                  id="email"
                  name="email"
                />
              </FormGroup>
              {isDataConsentNoticeActive && (
                <FormGroup
                  errors={{
                    dataConsentNotice:
                      isDataConsentNoticeErrorPresent &&
                      'Notice must be accepted',
                  }}
                  inputKey="dataConsentNotice"
                  labelHtmlFor="data-consent-notice"
                  required
                >
                  <div className="ApplySignUp__form__DataConsentNotice">
                    <CheckboxButtonGroup
                      className="ApplySignUp__form__DataConsentNotice__CheckboxButtonGroup"
                      defaultValue={isDataConsentNoticeChecked}
                    >
                      <FormControlLabel
                        bordered={false}
                        Control={() => (
                          <CheckboxButton
                            aria-describedby={
                              isDataConsentNoticeErrorPresent
                                ? 'form-errors-dataConsentNotice'
                                : null
                            }
                            aria-invalid={isDataConsentNoticeErrorPresent}
                            aria-required="true"
                            checked={dataConsentNoticeChecked}
                            id="data-consent-notice-checkbox"
                            name="accept_data_consent_notice"
                            value={1}
                            onChange={handleDCNCheckboxChange}
                          />
                        )}
                        id="data-consent-notice-checkbox"
                        text=""
                      >
                        <div
                          className="ApplySignUp__form__DataConsentNotice__content--scrolling"
                          dangerouslySetInnerHTML={{
                            __html: dataConsentNoticeText,
                          }}
                        />
                      </FormControlLabel>
                    </CheckboxButtonGroup>
                  </div>
                </FormGroup>
              )}
            </Card>

            <div className="ApplySignUp__form__submit__group">
              <FormGroup
                errors={{ timezone: errors.timezone_name }}
                inputKey="timezone"
                labelHtmlFor="timezone-name"
                required
              >
                <TimezoneSelect
                  defaultValue={timezoneName}
                  id="timezone-name"
                  isSearchable
                  menuPlacement="top"
                  name="timezone_name"
                  placeholder="Select timezone..."
                  useSystemFallback
                />
              </FormGroup>
              <Button
                className="ApplySignUp__form__continue-button"
                disabled={loading || isSubmitting}
                type="submit"
                variant="primary"
              >
                Continue to participate
              </Button>
              <input
                id="accept_terms"
                name="accept_terms"
                type="hidden"
                value="1"
              />
            </div>
          </DopeForm>
        </div>
      </CardStack>
    </main>
  );
}

ApplySignUp.propTypes = {
  dataConsentNoticeText: propTypes.string,
  email: propTypes.string,
  errors: propTypes.object,
  firstName: propTypes.string,
  isDataConsentNoticeActive: propTypes.bool,
  isDataConsentNoticeChecked: propTypes.bool,
  isDataConsentNoticeErrorPresent: propTypes.bool,
  lastName: propTypes.string,
  logoUrl: propTypes.string,
  projectAccessCode: propTypes.string,
  projectId: propTypes.number.isRequired,
  teamName: propTypes.string,
  timezoneName: propTypes.string,
};

export default ApplySignUp;
